import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { LeadCycleRuleApiService } from '../services/lead-cycle-rule-api.service';
import { LeadCycleRuleResponse } from '../models/lead-cycle-rule.model';
import { LeadCycleApiService } from '../../lead-cycles/services/lead-cycle-api.service';
import { LeadCycleResponse } from '../../lead-cycles/models/lead-cycle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';

type BadgeColor = 'primary' | 'success' | 'error' | 'warning' | 'info' | 'light' | 'dark';

@Component({
  selector: 'app-lead-cycle-rule-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    HasPermissionDirective
  ],
  templateUrl: './lead-cycle-rule-list.component.html'
})
export class LeadCycleRuleListComponent implements OnInit {
  reglas: LeadCycleRuleResponse[] = [];
  ciclos: LeadCycleResponse[] = [];
  loading = false;
  error = '';
  selectedCycleId: number | undefined = undefined;

  constructor(
    private ruleApi: LeadCycleRuleApiService,
    private cycleApi: LeadCycleApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarCiclos();
    this.cargarReglas();
  }

  cargarCiclos(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.getCycles(businessId).subscribe({
      next: (data) => {
        this.ciclos = data;
      },
      error: (err) => {
        console.error('Error al cargar ciclos:', err);
      }
    });
  }

  cargarReglas(): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.ruleApi.getRules({
      businessId,
      cycleId: this.selectedCycleId,
      page: 0,
      size: 100
    }).subscribe({
      next: (data) => {
        this.reglas = data.content;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las reglas de ciclo';
        this.loading = false;
        console.error('Error:', err);
      }
    });
  }

  onCycleFilterChange(): void {
    this.cargarReglas();
  }

  nuevaRegla(): void {
    this.router.navigate(['/config-leads/reglas/crear']);
  }

  editarRegla(regla: LeadCycleRuleResponse): void {
    this.router.navigate(['/config-leads/reglas/editar', regla.id]);
  }

  async eliminarRegla(regla: LeadCycleRuleResponse): Promise<void> {
    const label = regla.attributeDisplayName
      ? ` "${regla.attributeDisplayName}" en el ciclo "${regla.cycleName}"`
      : '';
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar regla',
      message: `¿Está seguro de eliminar la regla${label}?`
    });
    if (!ok) return;
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.ruleApi.deleteRule(regla.id, businessId).subscribe({
      next: () => {
        this.cargarReglas();
        this.toast.deleted(
          'Regla de ciclo',
          regla.cycleName
            ? `Se eliminó la regla del ciclo "${regla.cycleName}" correctamente.`
            : 'Se eliminó la regla de ciclo correctamente.'
        );
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar la regla' });
      }
    });
  }

  getActiveColor(isActive: boolean): BadgeColor {
    return isActive ? 'success' : 'error';
  }

  getActiveLabel(isActive: boolean): string {
    return isActive ? 'Activo' : 'Inactivo';
  }

  getOperationTypeLabel(operationType: string): string {
    const labels: Record<string, string> = {
      'SET': 'Asignar',
      'INCREMENT': 'Incrementar',
      'COPY_FROM_FIELD': 'Copiar campo'
    };
    return labels[operationType] || operationType;
  }

  getOperationTypeColor(operationType: string): BadgeColor {
    const colors: Record<string, BadgeColor> = {
      'SET': 'primary',
      'INCREMENT': 'info',
      'COPY_FROM_FIELD': 'warning'
    };
    return colors[operationType] || 'light';
  }
}
